import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import type { AuthMode } from '../types/auth'
import AuthForm from '../components/auth/AuthForm'
import { useAuthContext } from '../hooks/useAuthContext'

const AuthPage: React.FC = () => {
  const [mode, setMode] = useState<AuthMode>('signup')
  const navigate = useNavigate()
  const location = useLocation()

  const { login, signup, loginWithGoogle, isLoading, error, clearError, validateForm } =
    useAuthContext()

  // Get the intended destination from location state, or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard'

  // Handle form submission
  const handleSubmit = async (data: {
    email: string
    password: string
    confirmPassword?: string
  }) => {
    clearError()

    const errors = validateForm(data.email, data.password, data.confirmPassword, mode)
    if (Object.keys(errors).length > 0) {
      return
    }

    try {
      if (mode === 'signup') {
        await signup(data.email, data.password)
      } else {
        await login(data.email, data.password)
      }
      // Redirect to dashboard after successful auth
      navigate(from, { replace: true })
    } catch {
      // Error handling is managed by the useAuth hook
    }
  }

  const handleGoogleAuth = async () => {
    clearError()
    try {
      await loginWithGoogle()
      // Redirect to dashboard after successful auth
      navigate(from, { replace: true })
    } catch {
      // Error handling is managed by the useAuth hook
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-page-container">
        <div className="auth-page-content">
          {/* Logo/Branding section */}
          <div className="auth-page-header">
            <div className="auth-page-logo">
              <img src="/gist-g-logo.png" alt="Ask Anything" className="auth-logo" />
              <div className="auth-brand-name">
                <span>Ask Anything</span>
              </div>
            </div>
          </div>

          {/* Authentication form */}
          <div className="auth-page-form">
            <AuthForm
              mode={mode}
              onModeChange={setMode}
              onSubmit={handleSubmit}
              onGoogleAuth={handleGoogleAuth}
              isLoading={isLoading}
              error={error}
            />
          </div>

          {/* Back to home link */}
          <div className="auth-page-footer">
            <button onClick={() => window.history.back()} className="auth-page-back-link">
              ← Back to home
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthPage
